import React from 'react';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import StylishNewInput from 'components/DesignSystems/New/StylishNewInput';
import { Form } from 'react-bootstrap';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { CONTACT_CHANNEL_LABELS, requiresConsent } from 'utils/contactMethods';
import { validateContactValue } from './contactMethodDraft';
import type { ContactMethodDraft } from './contactMethodDraft';

const CHANNEL_HELP: Record<ContactChannel, string> = {
  email: 'Addresses we can email. The first is used unless it bounces.',
  sms: 'Numbers we can text. Reply STOP to any message to opt out.',
  voice: 'Numbers we can call with a recorded message, tried in this order.',
  pager: 'Pager addresses, in the form number@carrier.example.',
};

const PLACEHOLDER: Record<ContactChannel, string> = {
  email: 'name@example.gov',
  sms: '+1 202 555 0101',
  voice: '+1 202 555 0101',
  pager: '5550101@pager.example.com',
};

type ContactMethodEditorProps = {
  channel: ContactChannel;
  methods: ContactMethodDraft[];
  onChange: (methods: ContactMethodDraft[]) => void;
  disabled?: boolean;
};

/**
 * Add, remove and reorder one channel's addresses.
 *
 * Order is the point of this screen: the position of a row is the order a send
 * tries it in, which is what the single mobile/home pair in the old profile
 * blob could not express.
 */
const ContactMethodEditor: React.FC<ContactMethodEditorProps> = ({
  channel,
  methods,
  onChange,
  disabled = false,
}) => {
  const update = (index: number, patch: Partial<ContactMethodDraft>) =>
    onChange(
      methods.map((method, i) =>
        i === index ? { ...method, ...patch } : method
      )
    );

  const remove = (index: number) =>
    onChange(methods.filter((_, i) => i !== index));

  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= methods.length) return;
    const next = [...methods];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  const add = () =>
    onChange([
      ...methods,
      { channel, value: '', label: '', optedIn: false, optedOut: false },
    ]);

  return (
    <div className="mb-4">
      <div className="d-flex align-items-center justify-content-between">
        <label className="form-label mb-0">
          {CONTACT_CHANNEL_LABELS[channel]}
        </label>
        <StylishNewButton
          className="button button--link"
          type="button"
          onClick={add}
          disabled={disabled}
        >
          Add
        </StylishNewButton>
      </div>
      <div className="form-text mb-2">{CHANNEL_HELP[channel]}</div>

      {methods.length === 0 ? (
        <div className="fst-italic text-muted">
          None on file. We cannot reach you by{' '}
          {CONTACT_CHANNEL_LABELS[channel].toLowerCase()}.
        </div>
      ) : null}

      {methods.map((method, index) => {
        const error = validateContactValue(channel, method.value);
        return (
          <div
            className="d-flex align-items-start mb-2 gap-2"
            key={`${channel}-${index}`}
          >
            <div className="flex-grow-1">
              <StylishNewInput
                value={method.value}
                placeholder={PLACEHOLDER[channel]}
                disabled={disabled || method.optedOut}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  update(index, { value: e.target.value })
                }
              />
              {error ? (
                <span className="form-text form-error">{error}</span>
              ) : null}
              {method.optedOut ? (
                <span className="form-text">
                  Opted out by replying STOP. Reply START from this number to
                  resume messages.
                </span>
              ) : null}
            </div>
            <div style={{ width: '9rem' }}>
              <StylishNewInput
                value={method.label ?? ''}
                placeholder="Label"
                disabled={disabled}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  update(index, { label: e.target.value })
                }
              />
            </div>
            {requiresConsent(channel) ? (
              <div className="pt-2" style={{ width: '7rem' }}>
                <Form.Switch
                  id={`consent-${channel}-${index}`}
                  label="Consent"
                  checked={method.optedIn}
                  disabled={disabled || method.optedOut}
                  onChange={(e) => update(index, { optedIn: e.target.checked })}
                />
              </div>
            ) : null}
            <div className="d-flex pt-1">
              <StylishNewButton
                className="button button--link"
                type="button"
                title="Move up"
                aria-label={`Move ${CONTACT_CHANNEL_LABELS[channel]} entry up`}
                disabled={disabled || index === 0}
                onClick={() => move(index, -1)}
              >
                <SharedIcon iconName="arrow_upward" bold />
              </StylishNewButton>
              <StylishNewButton
                className="button button--link"
                type="button"
                title="Move down"
                aria-label={`Move ${CONTACT_CHANNEL_LABELS[channel]} entry down`}
                disabled={disabled || index === methods.length - 1}
                onClick={() => move(index, 1)}
              >
                <SharedIcon iconName="arrow_downward" bold />
              </StylishNewButton>
              <StylishNewButton
                className="button button--link"
                type="button"
                title="Remove"
                aria-label={`Remove ${CONTACT_CHANNEL_LABELS[channel]} entry`}
                disabled={disabled}
                onClick={() => remove(index)}
              >
                <SharedIcon iconName="delete" bold />
              </StylishNewButton>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ContactMethodEditor;
